import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Loader2, Search, Check, Clock, X as XIcon, ChevronLeft } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: {
    label: "قيد المراجعة",
    desc: "طلبك قيد المراجعة من قبل لجنة الإدارة. ستصلك رسالة فور اعتماد المشاركة.",
    icon: Clock,
    color: "amber",
    cls: "bg-amber-50 border-amber-300 text-amber-700",
    iconCls: "bg-amber-500",
  },
  approved: {
    label: "تم الاعتماد",
    desc: "تهانينا! تم اعتماد مشاركتك. اضغط على زر الباج لعرضه وطباعته.",
    icon: Check,
    color: "green",
    cls: "bg-green-50 border-green-300 text-green-700",
    iconCls: "bg-green-600",
  },
  rejected: {
    label: "تم الرفض",
    desc: "نأسف، لم تتم الموافقة على مشاركتك في هذه النسخة. للاستفسار يرجى التواصل مع إدارة النادي.",
    icon: XIcon,
    color: "red",
    cls: "bg-red-50 border-red-300 text-red-700",
    iconCls: "bg-rb-red",
  },
};

export default function Status() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();

  const lookup = async (refCode) => {
    if (!refCode || !refCode.trim()) {
      toast.error("يرجى إدخال رقم المرجع");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/registrations/lookup/${refCode.trim().toUpperCase()}`);
      setResult(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "تعذّر العثور على الطلب";
      toast.error(typeof msg === "string" ? msg : "خطأ");
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if ?ref= is present (e.g. from QR code on badge)
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setCode(ref.toUpperCase());
      lookup(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    lookup(code);
  };

  return (
    <div data-testid="status-page" className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-2xl w-full mx-auto px-6 py-16">
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-rb-red" />
            <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
              STATUS LOOKUP
            </span>
            <span className="h-[2px] w-10 bg-rb-red" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mb-4 text-rb-ink">
            استعلام عن طلبك
          </h1>
          <p className="text-rb-textMuted text-base leading-loose max-w-md mx-auto">
            أدخل رقم المرجع الذي حصلت عليه عند التسجيل لمعرفة حالة طلبك.
          </p>
        </div>

        <form onSubmit={submit} className="bg-rb-surface border border-rb-border p-6 mb-6" data-testid="status-form">
          <label className="block text-xs tracking-widest text-rb-textMuted uppercase mb-2">
            رقم المرجع
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              data-testid="status-code-input"
              placeholder="RB100-XXXXXX"
              className="flex-1 bg-white border border-rb-border focus:border-rb-red focus:outline-none focus:ring-2 focus:ring-rb-red/20 px-4 py-3 text-rb-ink text-sm font-mono tracking-widest transition-colors placeholder:text-rb-textMuted/70"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="status-submit-btn"
              className="bg-rb-red hover:bg-rb-redHover disabled:opacity-60 text-white font-bold px-6 py-3 transition-colors flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <Search className="w-5 h-5" />
                  <span className="hidden sm:inline">بحث</span>
                </>
              )}
            </button>
          </div>
        </form>

        {result && (
          <div data-testid="status-result" className="animate-fade-up bg-white border border-rb-border p-7 shadow-sm">
            <ResultCard data={result} />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const ResultCard = ({ data }) => {
  const cfg = STATUS_CONFIG[data.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <>
      <div className={`flex items-center gap-4 p-4 border ${cfg.cls} mb-6`}>
        <div className={`${cfg.iconCls} w-12 h-12 flex items-center justify-center text-white shrink-0`}>
          <Icon className="w-6 h-6" strokeWidth={3} />
        </div>
        <div>
          <div className="font-heading text-xl font-extrabold">{cfg.label}</div>
          <div className="text-xs mt-0.5 opacity-80">رقم المرجع: {data.reference_code}</div>
        </div>
      </div>

      <p className="text-rb-textMuted leading-loose text-sm mb-6">{cfg.desc}</p>

      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-rb-border">
        <Info label="الاسم" value={data.name} />
        <Info label="لوحة السيارة" value={data.plate_number} mono />
        <Info label="تاريخ التسجيل" value={new Date(data.created_at).toLocaleDateString("ar")} />
        {data.approved_at && (
          <Info label="تاريخ الاعتماد" value={new Date(data.approved_at).toLocaleDateString("ar")} />
        )}
      </div>

      {data.status === "approved" && (
        <Link
          to={`/badge/${data.reference_code}`}
          target="_blank"
          data-testid="status-view-badge"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-rb-red hover:bg-rb-redHover text-white font-bold px-6 py-3 transition-colors"
        >
          عرض الباج وطباعته
          <ChevronLeft className="w-5 h-5" />
        </Link>
      )}
    </>
  );
};

const Info = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] tracking-widest text-rb-textMuted uppercase mb-1">{label}</div>
    <div className={`text-sm text-rb-ink ${mono ? "font-mono tracking-widest" : ""}`}>{value}</div>
  </div>
);
